import { useMemo, useState } from "react";
import { dramas, dramaCategories } from "@/mocks/dramas";

type CatalogSectionProps = {
  activeCategory: string;
  onChangeCategory: (category: string) => void;
};

type SortKey = "heat" | "episodes" | "year";

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "heat", label: "按热度" },
  { key: "episodes", label: "按集数" },
  { key: "year", label: "按年份" },
];

export default function CatalogSection({ activeCategory, onChangeCategory }: CatalogSectionProps) {
  const [sortKey, setSortKey] = useState<SortKey>("heat");

  const list = useMemo(() => {
    const filtered =
      activeCategory === "全部"
        ? [...dramas]
        : dramas.filter((d) => d.category === activeCategory);
    return filtered.sort((a, b) => {
      if (sortKey === "heat") return b.heat - a.heat;
      if (sortKey === "episodes") return b.episodes - a.episodes;
      return b.year - a.year;
    });
  }, [activeCategory, sortKey]);

  return (
    <section id="catalog" className="w-full border-b border-background-200 bg-background-50">
      <div className="mx-auto w-full max-w-[1240px] px-4 py-12 md:px-8 md:py-16">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <span className="text-xs font-semibold uppercase tracking-widest text-accent-700">Catalog</span>
            <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">短剧合集</h2>
            <p className="mt-2 max-w-2xl text-sm text-foreground-600">
              共收录 {dramas.length} 部岳母题材剧情演绎合集。先按题材筛一遍，再用标签判断具体切入点。
            </p>
          </div>
          <div className="flex items-center gap-1 rounded-full border border-background-300 bg-background-100 p-1">
            {sortOptions.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setSortKey(opt.key)}
                className={`cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition-colors ${
                  sortKey === opt.key
                    ? "bg-secondary-500 text-background-50"
                    : "text-foreground-600 hover:bg-background-200"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {dramaCategories.map((cat) => (
            <button
              key={cat}
              type="button"
              onClick={() => onChangeCategory(cat)}
              aria-pressed={activeCategory === cat}
              className={`cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                activeCategory === cat
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-300 bg-background-100 text-foreground-600 hover:border-primary-300 hover:text-primary-700"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        <p className="mt-4 text-xs text-foreground-500">
          当前筛选：<span className="font-medium text-foreground-800">{activeCategory}</span> · 共{" "}
          {list.length} 条结果
        </p>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((drama) => (
            <article
              key={drama.id}
              className="group flex flex-col rounded-lg border border-background-200 bg-background-100/60 p-5 transition-colors hover:border-primary-300 hover:bg-background-50"
            >
              <div className="flex items-start justify-between gap-3">
                <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-900">
                  <span className="flex h-3.5 w-3.5 items-center justify-center">
                    <i className="ri-price-tag-3-line text-xs" aria-hidden="true" />
                  </span>
                  {drama.category}
                </span>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs ${
                    drama.status === "已完结"
                      ? "bg-secondary-100 text-secondary-800"
                      : "bg-primary-100 text-primary-800"
                  }`}
                >
                  {drama.status}
                </span>
              </div>

              <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950 transition-colors group-hover:text-primary-700">
                {drama.title}
              </h3>

              <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-foreground-600">
                {drama.summary}
              </p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {drama.tags.map((tag) => (
                  <span key={tag} className="rounded-md border border-background-300 px-2 py-0.5 text-xs text-foreground-500">
                    #{tag}
                  </span>
                ))}
              </div>

              <dl className="mt-4 grid grid-cols-3 gap-2 border-t border-background-200 pt-4 text-xs">
                <div>
                  <dt className="text-foreground-500">集数</dt>
                  <dd className="mt-0.5 font-medium text-foreground-900">{drama.episodes} 集</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">时长</dt>
                  <dd className="mt-0.5 font-medium text-foreground-900">{drama.duration}</dd>
                </div>
                <div>
                  <dt className="text-foreground-500">热度</dt>
                  <dd className="mt-0.5 font-medium text-foreground-900">{drama.heat.toLocaleString()}</dd>
                </div>
              </dl>
            </article>
          ))}
        </div>

        {list.length === 0 && (
          <div className="mt-6 rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-12 text-center">
            <p className="text-sm text-foreground-600">该题材下暂时没有收录，换个分类看看？</p>
          </div>
        )}
      </div>
    </section>
  );
}